import React from "react";
import { Box, Flex } from "theme-ui";

import { mnt } from "theme/maintenanceTokens";

/**
 * Plan Detail section header (mockup 28 `.h2` + `.body-muted` + `.link`).
 *
 * WHY THIS EXISTS: every section on Plan Detail (Batches, Active Work
 * Orders, Locations, Plan Reference) drew its own header \u2014 mixed-case
 * 14px H5 labels with ad-hoc helper text and action links, three
 * different treatments down one page. The Work Order case file (mockup
 * 27) reads as a tight stack of UPPERCASE-labelled sections (INTAKE,
 * PRICING, ATTACHED RECORDS); Plan Detail did not share that rhythm, so
 * it read looser and less finished beside it. This is the one header
 * every plan section uses so the page reads as one console.
 *
 * Shape: an uppercase section label, an optional muted inline hint (the
 * count / scope note the mockup carries next to each heading), and an
 * optional right-aligned action node (a "+ New batch" link, a filter
 * control, whatever the section owns).
 */
export default function PlanSectionHeader({
  label,
  hint,
  action,
  sx,
}: {
  label: string;
  hint?: React.ReactNode;
  action?: React.ReactNode;
  sx?: any;
}) {
  return (
    <Flex
      sx={{
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "12px",
        marginBottom: "12px",
        ...(sx || {}),
      }}
    >
      <Flex sx={{ alignItems: "baseline", gap: "10px", minWidth: 0, flexWrap: "wrap" }}>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            color: mnt.textMuted,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
        {hint != null && hint !== "" && (
          <Box
            sx={{
              fontFamily: "Poppins",
              fontSize: "11px",
              color: mnt.textFaint,
              minWidth: 0,
            }}
          >
            {hint}
          </Box>
        )}
      </Flex>
      {action != null && <Box sx={{ flex: "0 0 auto" }}>{action}</Box>}
    </Flex>
  );
}
